/**
 * 移动端适配工具函数
 */

/**
 * 检测当前设备是否为移动设备
 * @returns {boolean} 是否为移动设备
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * 检测当前设备是否为微信浏览器
 * @returns {boolean} 是否为微信浏览器
 */
export const isWechat = () => {
  return /MicroMessenger/i.test(navigator.userAgent);
};

/**
 * 检测当前设备是否为iOS设备
 * @returns {boolean} 是否为iOS设备
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * 检测当前设备是否为Android设备
 * @returns {boolean} 是否为Android设备
 */
export const isAndroid = () => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * 获取当前设备的屏幕方向
 * @returns {string} 'portrait' 或 'landscape'
 */
export const getOrientation = () => {
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

/**
 * 监听屏幕方向变化
 * @param {Function} callback 回调函数，参数为当前方向 'portrait' 或 'landscape'
 * @returns {Function} 移除监听器的函数
 */
export const onOrientationChange = (callback) => {
  const handler = () => {
    callback(getOrientation());
  };
  window.addEventListener('resize', handler);
  return () => {
    window.removeEventListener('resize', handler);
  };
};

/**
 * px转换为vw
 * @param {number} px 像素值
 * @param {number} viewportWidth 设计稿宽度，默认为375
 * @returns {string} vw值
 */
export const pxToVw = (px, viewportWidth = 375) => {
  return `${(px / viewportWidth) * 100}vw`;
};

/**
 * 禁止页面缩放
 */
export const preventZoom = () => {
  document.addEventListener('touchstart', (event) => {
    if (event.touches.length > 1) {
      event.preventDefault();
    }
  }, { passive: false });
  
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (event) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
};

/**
 * 修复iOS输入框获取焦点时页面放大问题
 */
export const fixIOSInput = () => {
  if (!isIOS()) return;
  
  const meta = document.querySelector('meta[name="viewport"]');
  if (meta) {
    const content = meta.getAttribute('content');
    
    document.body.addEventListener('focusin', () => {
      meta.setAttribute('content', content.replace(/user-scalable=no/gi, 'user-scalable=yes'));
    });
    
    document.body.addEventListener('focusout', () => {
      meta.setAttribute('content', content);
    });
  }
};

/**
 * 修复iOS橡皮筋效果
 */
export const fixIOSBounce = () => {
  if (!isIOS()) return;
  
  document.body.addEventListener('touchmove', (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  }, { passive: false });
};

/**
 * 设置rem基准值
 * @param {number} designWidth 设计稿宽度，默认为375
 */
export const setRem = (designWidth = 375) => {
  const docEl = document.documentElement;
  const resizeEvt = 'orientationchange' in window ? 'orientationchange' : 'resize';
  
  const recalc = () => {
    const clientWidth = docEl.clientWidth;
    if (!clientWidth) return;
    
    // 设置1rem = 设计稿宽度/10，这样1rem就等于37.5px
    // 微信浏览器需要特殊处理，缩放因子调整为0.95
    if (isWechat()) {
      docEl.style.fontSize = (clientWidth / designWidth) * 10 * 0.95 + 'px';
    } else {
      docEl.style.fontSize = (clientWidth / designWidth) * 10 + 'px';
    }
  };
  
  if (!document.addEventListener) return;
  window.addEventListener(resizeEvt, recalc, false);
  document.addEventListener('DOMContentLoaded', recalc, false);
  recalc();
};

/**
 * px转换为rem（用于开发阶段手动计算）
 * @param {number} px 像素值
 * @returns {string} rem值
 */
export const pxToRem = (px) => {
  // 基于设计稿宽度/10作为1rem的值
  return (px / 37.5) + 'rem';
};

/**
 * 为微信浏览器添加特定样式
 */
export const applyWechatStyles = () => {
  if (!isWechat()) return;
  
  // 添加微信浏览器标识类
  document.documentElement.classList.add('wechat-browser');
  
  // 创建微信专用样式
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = `
    /* 微信浏览器特定样式 */
    .wechat-browser .mobile-content {
      /* 修复微信中的内容区域显示问题 */
      overflow-x: hidden;
    }
    .wechat-browser img {
      /* 修复微信中的图片缩放问题 */
      max-width: 100%;
      height: auto;
    }
    .wechat-browser input, .wechat-browser textarea {
      /* 修复微信中的输入框问题 */
      -webkit-appearance: none;
      appearance: none;
    }
  `;
  document.head.appendChild(style);
};

/**
 * 初始化移动端适配
 */
export const initMobileAdapter = () => {
  if (isMobile()) {
    // 修复iOS输入框问题
    fixIOSInput();
    
    // 修复iOS橡皮筋效果
    fixIOSBounce();
    
    // 设置rem基准值
    setRem(375);
    
    // 应用微信浏览器特定样式
    applyWechatStyles();
  }
};